// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Button from "@insite/mobius/Button";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import ListItem, { OrderedList, UnorderedList } from "@insite/mobius/Lists";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import omitSingle from "@insite/mobius/utilities/omitSingle";
import { extractParagraphStyle } from "@vanmeter/XDL/Utilities/helpers";
import { domToReact, HTMLReactParserOptions } from "html-react-parser";
import React from "react";

/**
 * Override parserOptions to support "Button" type
 */
export const xParserOptions: HTMLReactParserOptions = {
    replace: (node: any) => {
        const { name, children, attribs = { style: "" }, ...rest } = node;
        const { style, class: classNames, ...otherAttribs } = attribs;

        const { className, variant } = extractParagraphStyle(classNames);
        otherAttribs.className = className;

        if (name && ["h1", "h2", "h3", "h4", "h5", "h6", "body", "p", "span", "legend"].includes(name) && children) {
            return (
                <Typography
                    css={style as any}
                    {...otherAttribs}
                    as={name as TypographyProps["as"]}
                    variant={variant || (name as TypographyProps["as"])}
                >
                    {children && domToReact(children, xParserOptions)}
                </Typography>
            );
        }
        if (name === "ul") {
            return (
                <UnorderedList css={style as any} {...otherAttribs}>
                    {children && domToReact(children, xParserOptions)}
                </UnorderedList>
            );
        }
        if (name === "ol") {
            return (
                <OrderedList css={style as any} {...otherAttribs}>
                    {children && domToReact(children, xParserOptions)}
                </OrderedList>
            );
        }
        if (name === "li") {
            return (
                <ListItem css={style as any} {...otherAttribs}>
                    {children && domToReact(children, xParserOptions)}
                </ListItem>
            );
        }
        if (name === "img") {
            const { alt, ...remainingAttribs } = otherAttribs;
            return (
                <LazyImage
                    as="span"
                    css={style as any}
                    altText={alt || `${otherAttribs.name} Image`}
                    {...remainingAttribs}
                />
            );
        }
        if (name === "a") {
            return (
                <Link
                    css={style as any}
                    id={otherAttribs.name}
                    aria-label={`${otherAttribs.name} link`}
                    className={`${otherAttribs.className} rich-content-link`}
                    {...omitSingle(otherAttribs, "className")}
                >
                    {children && domToReact(children, xParserOptions)}
                </Link>
            );
        }
        // XNG-Changes: include button to parse
        if (name === "button") {
            return (
                <Button
                    css={style as any}
                    id={otherAttribs.name}
                    aria-label={`${otherAttribs.name} button`}
                    {...otherAttribs}
                >
                    {children && domToReact(children, xParserOptions)}
                </Button>
            );
        }
    },
};
